/**
 * Scrollytelling component
 *
 * Desktop (≥750px):
 * - Media items scroll naturally.
 * - Content column stays sticky via CSS.
 * - Active content crossfades when the corresponding media item's
 *   center reaches the viewport center.
 *
 * Mobile (<750px):
 * - No JS behavior.
 */

const GSAP_CDN =
  'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js';

const ST_CDN =
  'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js';

const BREAKPOINT = 750;

async function loadGSAP() {
  if (window.__gsapLoaded) return;

  const load = (src) =>
    new Promise((resolve, reject) => {
      const script = document.createElement('script');

      script.src = src;
      script.onload = resolve;
      script.onerror = reject;

      document.head.appendChild(script);
    });

  await load(GSAP_CDN);
  await load(ST_CDN);

  window.gsap.registerPlugin(window.ScrollTrigger);
  window.__gsapLoaded = true;
}

class ScrollytellingComponent extends HTMLElement {
  #triggers = [];

  #mql = window.matchMedia(`(min-width: ${BREAKPOINT}px)`);

  connectedCallback() {
    if (this.hasAttribute('data-design-mode')) return;

    if (this.#mql.matches) {
      this.#init();
    }

    this.#mql.addEventListener('change', this.#onBreakpoint);
  }

  disconnectedCallback() {
    this.#mql.removeEventListener('change', this.#onBreakpoint);
    this.#destroy();
  }

  #onBreakpoint = ({ matches }) => {
    if (matches) {
      this.#init();
    } else {
      this.#destroy();
    }
  };

  async #init() {
    await loadGSAP();

    const { gsap, ScrollTrigger } = window;

    const debug = this.hasAttribute('data-debug');

    const mediaItems = [
      ...this.querySelectorAll(
        '.scrollytelling__media-col .scrollytelling__media-item'
      ),
    ];

    const contentItems = [
      ...this.querySelectorAll(
        '.scrollytelling__content-col .scrollytelling__content-item'
      ),
    ];

    if (!mediaItems.length || !contentItems.length) {
      return;
    }

    // Initial state
    gsap.set(contentItems, {
      autoAlpha: 0,
    });

    gsap.set(contentItems[0], {
      autoAlpha: 1,
    });

    mediaItems.forEach((media, index) => {
      const trigger = ScrollTrigger.create({
        trigger: media,
        // Scroll down: cross start → activate this slide
        // Scroll up:   cross end   → activate this slide (onEnterBack)
        // onLeaveBack (cross start going up) intentionally has no handler —
        // the previous slide stays active until its end is crossed on the way up.
        start: 'top center',
        end: 'bottom center',
        markers: debug,
        onEnter: () => this.#activate(contentItems, index),
        onEnterBack: () => this.#activate(contentItems, index),
      });

      this.#triggers.push(trigger);
    });

    // Lazy-loaded images above this section push it down after GSAP first
    // measures positions. Refresh once the entire page has settled.
    if (document.readyState === 'complete') {
      ScrollTrigger.refresh();
    } else {
      window.addEventListener('load', () => ScrollTrigger.refresh(), { once: true });
    }
  }

  #activate(contentItems, activeIndex) {
    const { gsap } = window;

    gsap.killTweensOf(contentItems);

    contentItems.forEach((item, index) => {
      gsap.to(item, {
        autoAlpha: index === activeIndex ? 1 : 0,
        duration: 0.25,
        ease: 'power2.out',
        overwrite: true,
      });
    });
  }

  #destroy() {
    const { gsap } = window;

    this.#triggers.forEach((trigger) => trigger.kill());
    this.#triggers = [];

    const contentItems = this.querySelectorAll(
      '.scrollytelling__content-item'
    );

    if (gsap) {
      gsap.killTweensOf(contentItems);
    }

    contentItems.forEach((item, index) => {
      item.style.removeProperty('opacity');
      item.style.removeProperty('visibility');

      if (index === 0) {
        item.style.opacity = '1';
      }
    });
  }
}

if (!customElements.get('scrollytelling-component')) {
  customElements.define(
    'scrollytelling-component',
    ScrollytellingComponent
  );
}